import _ from 'lodash'

export const isMediaType = (item) => {
  return ['image', 'pdf', 'video', 'audio'].indexOf(item.type) !== -1
}

export const isTextType = (item) => {
  return ['paragraph'].indexOf(item.type) !== -1
}

export const isRowType = (item) => {
  return item.type === 'row'
}

export const isChild = ({state, id}) => {
  return state.editing.rows[id] === undefined
}

/**
 * Add general fields for editing when creating an item.
 */
const makeRowItem = (item) => {
  return {
    ...item,
    text: '',
    is_procedure: false,
    children: []
  }
}

/**
 * Add the form field for media type items.
 */
const addForm = (item) => {
  return {
    ...item,
    form: {
      success: false,
      fields: {
        name: null,
        file: null,
        thumbnail: null
      },
      apiErrors: {
        name: [],
        file: [],
        thumbnail: []
      },
      saving: false
    }
  }
}

/**
 * Add the api errors field.
 */
const addApiErrors = (item) => {
  return {
    ...item,
    apiErrors: []
  }
}

/**
 * Add fields required for text type items.
 */
const makeTextItem = (item) => {
  return {
    ...item,
    text: ''
  }
}

/**
 * Add fields required for media type items.
 */
const makeMediaItem = (item) => {
  item = {
    ...item,
    media: []
  }

  return addForm(item)
}

/**
 * Add any fields required for editing.
 */
export const mapForEditing = ({item, parentID = null} = {}) => {
  item = {
    ...item,
    apiErrors: [],
    is_saved: true
  }

  if (isMediaType(item)) {
    item = addForm(item)
  }

  if (parentID) {
    item = {
      ...item,
      parentID
    }
  }

  return item
}

/**
 * Set prop(s) on an item.
 */
export const updateItem = ({state, id, props}) => {
  if (isChild({state, id})) {
    state.editing.children[id] = {
      ...state.editing.children[id],
      ...props
    }
  } else {
    state.editing.rows[id] = {
      ...state.editing.rows[id],
      ...props
    }
  }
}

/**
 * Set a prop on an item.
 */
export const setProp = ({state, id, prop, value}) => {
  if (isChild({state, id})) {
    _.set(state.editing.children[id], prop, value)
  } else {
    _.set(state.editing.rows[id], prop, value)
  }
}

/**
 * Create an item object.
 */
export const createItem = ({state, type, parentID}) => {
  // get a list of existing ids so we can generate a new unique one
  const rowItemIDs = Object.keys(state.editing.children)
  const ids = state.editing.ids.concat(rowItemIDs)

  let item = {
    id: uuid(ids),
    is_saved: false,
    type
  }

  if (parentID) {
    item.parentID = parentID
  }

  item = addApiErrors(item)

  if (isMediaType(item)) {
    item = makeMediaItem(item)
  } else if (isTextType(item)) {
    item = makeTextItem(item)
  } else if (isRowType(item)) {
    item = makeRowItem(item)
  }

  return item
}

/**
 * Inserts an item given an insert position specified by
 * a parent id and a next id.
 */
export const insertItem = ({state, parentID, nextID, item}) => {

  // apend to items
  if (parentID === null && nextID === null) {
    state.editing.rows = {
      ...state.editing.rows,
      [item.id]: item
    }

    state.editing.ids.push(item.id)

  // append to row
  } else if (parentID && nextID === null) {
    state.editing.children = {
      ...state.editing.children,
      [item.id]: item
    }

    state.editing.rows[parentID].children.push(item.id)

  // insert into items
  } else if (parentID === null && nextID) {

    state.editing.rows = {
      ...state.editing.rows,
      [item.id]: item
    }

    const idx = state.editing.ids.indexOf(nextID)
    state.editing.ids.splice(idx, 0, item.id)

  // insert into a row
  } else if (parentID && nextID) {
    state.editing.children = {
      ...state.editing.children,
      [item.id]: item
    }

    const idx = state.editing.rows[parentID].children.indexOf(nextID)
    state.editing.rows[parentID].children.splice(idx, 0, item.id)
  }
}

/**
 * Denormalizes the items being edited.
 */
export const denormalizeRowItems = ({state, items, editing}) => {
  const denormalized = []

  // ensure we are not modifying state
  items = JSON.parse(JSON.stringify(items))

  items.forEach((item) => {
    if (isRowType(item)) {
      if (editing) {
        item.children = item.children.map((id) => state.editing.children[id])
      } else {
        item.children = item.children.map((id) => state.index.children[id])
      }
    }
    denormalized.push(item)
  })

  return denormalized
}

/**
 * Make a UUID.
 */
const generateUUID = () => {
  let d = new Date().getTime()
  if (typeof performance !== 'undefined' && typeof performance.now === 'function'){
    d += performance.now()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (d + Math.random() * 16) % 16 | 0
    d = Math.floor(d / 16)
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
  })
}

/**
 * Create a UUID that does not already exist.
 */
export const uuid = (uuids) => {
  let uuid

  do {
    uuid = generateUUID()
  } while(uuids.indexOf(uuid) !== -1)

  return uuid
}

/**
 * Format editing rows for saving to api.
 */
export const upsertRowsRequest = (state) => {
  const rows = state.editing.ids.map((id) => state.editing.rows[id])
  const requestData = []
  state.apiRequestOrder = []

  for (let i=0; i<rows.length; i++) {
    const row = rows[i]

    const mappedRow = {
      type: row.type,
      order: i,
      text: row.text,
      is_procedure: row.is_procedure
    }

    if (row.is_saved) {
      mappedRow.id = row.id
    }

    requestData.push(mappedRow)
    state.apiRequestOrder.push(row.id)
  }

  return requestData
}

/**
 * Format editing children for saving to api.
 */
export const upsertChildrenRequest = (state) => {

  let rows = state.editing.ids.map((id) => state.editing.rows[id])
  rows = denormalizeRowItems({state, items: rows, editing: true})

  const requestData = []
  state.apiRequestOrder = []

  for (let i=0; i<rows.length; i++) {
    const row = rows[i]
    for (let j=0; j<row.children.length; j++) {
      const child = row.children[j]

      const mappedChild = {
        type: child.type,
        row_id: row.id,
        order: j
      }

      if (isTextType(child)) {
        mappedChild.text = child.text
      } else if (isMediaType(child)) {
        mappedChild.media_id = child.media[0] ? child.media[0].id : null
      }

      if (child.is_saved) {
        mappedChild.id = child.id
      }

      requestData.push(mappedChild)
      state.apiRequestOrder.push(child.id)
    }
  }

  return requestData
}

/**
 * Map errors to human friendly errors.
 * TODO handle other types of api errors as needed.
 */
export const mapError = (errors) => {
  for (let i = 0; i < errors.length; i++) {
    const error = errors[i]
    if (error.match(/required/)) {
      if (error.match(/image/)) {
        errors[i] = 'An image is required'
      } else if (error.match(/video/)) {
        errors[i] = 'A video is required'
      } else if (error.match(/pdf/)) {
        errors[i] = 'A pdf is required'
      } else if (error.match(/audio/)) {
        errors[i] = 'An audio file is required'
      } else {
        errors[i] = 'This field is required'
      }
    }
  }

  return errors
}

/**
 * Takes a denormalized array of items and strips all but certain fields
 * used to compare sets of items.
 */
export const stipFieldsForDiff = (rows) => {
  const stripped = []

  rows.forEach((row) => {
    row = {
      id: row.id,
      is_procedure: row.is_procedure,
      order: row.order,
      text: row.text,
      children: row.children
    }

    const strippedRowItems = []

    row.children.forEach((child) => {
      strippedRowItems.push({
        id: child.id,
        order: child.order,
        type: child.type,
        media: child.media,
        text: child.text
      })
    })

    row.children = strippedRowItems

    stripped.push(row)
  })

  return stripped
}

// ----------------------------------------
// Specifically for client view
// ----------------------------------------

/**
 * Repeated media are grouped together in the view Find repeated media in an
 * array of items and group them into a new item type called 'media_group' The
 * items array is expected to be in the format the api provides
 */
export const groupMediaItems = (items) => {
  // ensure we are not modifying state
  items = JSON.parse(JSON.stringify(items))

  const grouped = []
  let media_items = []

  for (let i = 0; i < items.length; i++) {
    const item = items[i]

    // item is media type
    if (isMediaType(item)) {
      media_items.push(item)

    // item is not media type
    } else {

      // there were previous media types
      if (media_items.length > 0) {
        grouped.push({
          media_items,
          type: 'media-group'
        })
        media_items = []
      }

      grouped.push(item)
    }

    if (
      i === items.length -1 && // last item
      media_items.length > 0 // there were previous media types
    ) {
      grouped.push({
        media_items,
        type: 'media-group'
      })
    }
  }

  return grouped
}



// WEBPACK FOOTER //
// ./src/store/modules/items/helpers/index.js